import type { DataGroup } from '@/widgets/TreeDataGrid';
import type { DimensionFilter, MetricFilter } from '@/features/filters';
import type { ColumnsConfig } from '@/entities/columnsConfig';
import { baseApi, getErrorMessage } from '@/shared/api';
import type {
  DateScale,
  FilterDateRangeDTO,
  Lifetime,
  Sorting,
} from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import type { DataResponse, Meta, TreeDataResponse } from '../model/types';
import type { MetaDTO } from './types';

export const getMeta = async (): Promise<Meta> => {
  const { data, error } = await baseApi.functions.invoke<MetaDTO>(
    'getCohortScannerMeta',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return {
    ...data,
    config: Object.fromEntries(
      Object.entries(data.config).map(([key, value]) => {
        return [
          key,
          {
            group: value.ui_column_group,
            name: value.ui_column_name,
            description: value.ui_column_description,
            type: value.ui_column_format,
            order: value.ui_base_view_drawer_filter_order,
            dataType: value.ui_column_data_type,
            isAdvancedSortable: value.ui_is_advanced_sorting_available,
          },
        ];
      }),
    ) as ColumnsConfig,
  };
};

export interface GetDataParams {
  dimensions: string[];
  metric: string;
  dateScale: DateScale;
  dateRange: FilterDateRangeDTO;
  offset: number;
  sort: Sorting;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
  dimensionsFilters: DimensionFilter[];
  metricsFilters: MetricFilter[];
  pageSize?: number;
}

export const getData = async ({
  dimensions,
  metric,
  dateScale,
  dateRange,
  offset,
  sort,
  probabilisticAttribution,
  lifetime,
  dimensionsFilters,
  metricsFilters,
  pageSize,
}: GetDataParams) => {
  const { data, error } = await baseApi.functions.invoke<DataResponse>(
    'getCohortScannerRows',
    {
      body: {
        dimensions,
        metric,
        dateScale,
        dateRange,
        offset,
        sort,
        probabilisticAttribution,
        lifetime,
        dimensionsFilters,
        metricsFilters,
        pageSize,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

interface GetTotalParams {
  dimensions: string[];
  metric: string;
  dateRange: FilterDateRangeDTO;
  dateScale: DateScale;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
  dimensionsFilters: DimensionFilter[];
}

export const getTotal = async ({
  dimensions,
  metric,
  dimensionsFilters,
  lifetime,
  dateRange,
  dateScale,
  probabilisticAttribution,
}: GetTotalParams) => {
  const { data, error } = await baseApi.functions.invoke<TotalData>(
    'getCohortScannerTotal',
    {
      body: {
        dimensions,
        metric,
        dateScale,
        dateRange,
        probabilisticAttribution,
        lifetime,
        dimensionsFilters,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

interface GetMinMaxParams {
  dimensions: string[];
  metric: string;
  dateRange: FilterDateRangeDTO;
  dateScale: DateScale;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
  dimensionsFilters: DimensionFilter[];
  metricsFilters: MetricFilter[];
  groupedData: boolean;
}

export const getMinMax = async (params: GetMinMaxParams) => {
  const { data, error } = await baseApi.functions.invoke<{
    min: number;
    max: number;
  }>('getCohortScannerMinMax', {
    body: {
      dimensions: params.dimensions,
      metric: params.metric,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      dateRange: params.dateRange,
      dateScale: params.dateScale,
      probabilisticAttribution: params.probabilisticAttribution,
      lifetime: params.lifetime,
      groupedData: params.groupedData,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

interface GetTreeDataParams {
  dimensions: [string];
  metric: string;
  dateScale: DateScale;
  dateRange: FilterDateRangeDTO;
  sort: Sorting;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
  dimensionsFilters: DimensionFilter[];
  metricsFilters: MetricFilter[];
  parentGroups: DataGroup[];
}

export const getTreeData = async ({
  dimensions,
  metric,
  dateScale,
  dateRange,
  sort,
  probabilisticAttribution,
  lifetime,
  dimensionsFilters,
  metricsFilters,
  parentGroups,
}: GetTreeDataParams) => {
  const { data, error } = await baseApi.functions.invoke<TreeDataResponse>(
    'getCohortScannerTreeViewData',
    {
      body: {
        dimensions,
        metric,
        dateScale,
        dateRange,
        sort,
        probabilisticAttribution,
        lifetime,
        dimensionsFilters,
        metricsFilters,
        parentGroups,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};
